import { get, values } from "lodash-es";
import type {
  PlayerGameStats,
  PlayerGameLogTeam,
} from "@/components/headToHead/types";
import type {
  GetHeadToHeadPlayerGameLogQuery,
  GetHeadToHeadPlayerGameStatsQuery,
  Maybe,
  TeamGameLog,
} from "@/graphql/gql/graphql";

const DEFAULT_STATS = {
  passAttempts: "-",
  passTDs: "-",
  passYards: "-",
  rushes: "-",
  rushTDs: "-",
  rushYards: "-",
  receptions: "-",
  recTDs: "-",
  recYards: "-",
  fgMade: "-",
  totalPoints: "-",
  xpMade: "-",
  ints: "-",
  sacks: "-",
  tacklesForLoss: "-",
};

const DEFAULT_GAME_LOG = {
  eventId: 0,
  timestamp: 0,
  team: {
    homeTeam: undefined,
    opponentTeam: { abbreviation: "-", logoSVG: "" },
  },
  score: "-",
};

const getPlayerInfo = (
  statsData: GetHeadToHeadPlayerGameStatsQuery["player"]
) => ({
  playerTeamId: statsData?.teamInfo?.numberFireId ?? 0,
  playerStats: statsData?.gameStats ?? [],
});

/**
 * `Event` node returns team numberfireId as a string but
 * `Player` node returns team numberfireId as a number
 */
const findTeamGameLogByPlayer = (
  logData: GetHeadToHeadPlayerGameLogQuery["event"],
  playerTeamId: number
) => {
  const teams = values(logData) as PlayerGameLogTeam[];
  return (
    teams.find((team) => Number(team?.numberfireId) === playerTeamId)
      ?.gameLog ?? []
  );
};

const mapGameLogToTableData = (gameLog: Maybe<TeamGameLog>[]) =>
  gameLog.map((game) => ({
    eventId: game?.gameId ?? 0,
    timestamp: game?.timestamp ?? 0,
    team: {
      homeTeam: game?.homeTeam,
      opponentTeam: {
        abbreviation: game?.opponentTeam?.abbreviation ?? "-",
        logoSVG: game?.opponentTeam?.logoSVG ?? "-",
      },
    },
    score: game?.score ?? "-",
  }));

/**
 * `Event` node returns eventId as number but
 * `Player` node returns eventId as string
 */
const mapPlayerStatsToTableData = (playerStats: PlayerGameStats) =>
  playerStats.map((stats) => ({
    eventId: Number(stats?.event?.numberfireId ?? 0),
    passAttempts: get(stats, "passAttempts") ?? 0,
    passTDs: get(stats, "passTDs") ?? 0,
    passYards: get(stats, "passYards") ?? 0,
    rushes: get(stats, "rushes") ?? 0,
    rushTDs: get(stats, "rushTDs") ?? 0,
    rushYards: get(stats, "rushYards") ?? 0,
    receptions: get(stats, "receptions") ?? 0,
    recTDs: get(stats, "recTDs") ?? 0,
    recYards: get(stats, "recYards") ?? 0,
    fgMade: get(stats, "fgMade") ?? 0,
    totalPoints: get(stats, "totalPoints") ?? 0,
    xpMade: get(stats, "xpMade") ?? 0,
    ints: get(stats, "ints") ?? 0,
    sacks: get(stats, "sacks") ?? 0,
    tacklesForLoss: get(stats, "tacklesForLoss") ?? 0,
  }));

const findGameLogById = (
  gameLogTableData: ReturnType<typeof mapGameLogToTableData>,
  eventId: number
) =>
  gameLogTableData?.find((game) => game?.eventId === eventId) ??
  DEFAULT_GAME_LOG;

const findStatsById = (
  statsTableData: ReturnType<typeof mapPlayerStatsToTableData>,
  eventId: number
) =>
  statsTableData.find((stats) => stats.eventId === eventId) ?? {
    eventId,
    ...DEFAULT_STATS,
  };

export const getTableData = (
  logData: GetHeadToHeadPlayerGameLogQuery["event"],
  statsData: GetHeadToHeadPlayerGameStatsQuery["player"]
) => {
  // Extract player information from `Player` node data
  const { playerTeamId, playerStats } = getPlayerInfo(statsData);

  // Extract team game log from `Event` node data using the player's team ID
  const teamGameLog = findTeamGameLogByPlayer(logData, playerTeamId);

  // Map the game log and player stats to table data
  const gameLogTableData = mapGameLogToTableData(teamGameLog);
  const playerStatsTableData = mapPlayerStatsToTableData(playerStats);

  // Extract game IDs from the team game log
  const gameIds = teamGameLog.map((game) => game?.gameId ?? 0);

  // For each game, combine game log and player stats table data
  return gameIds.map((gameId) => ({
    ...findGameLogById(gameLogTableData, gameId),
    ...findStatsById(playerStatsTableData, gameId),
  }));
};
